import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Loads Stockfish in a Web Worker and exposes bestMove(fen, {level, movetime}).
 * Level 0..20 sets Skill Level. Returns { ready, thinking, bestMove }.
 */
export default function useChessEngine() {
  const engineRef = useRef(null);
  const resolverRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let engine;
    try {
      const workerCode = `importScripts('https://cdn.jsdelivr.net/npm/stockfish.js@10.0.2/stockfish.js');`;
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      engine = new Worker(URL.createObjectURL(blob));
      engineRef.current = engine;
      engine.onmessage = (e) => {
        const line = typeof e.data === 'string' ? e.data : (e.data?.data || '');
        if (line === 'uciok') engine.postMessage('isready');
        else if (line === 'readyok') setReady(true);
        else if (line.startsWith('bestmove')) {
          const uci = line.split(' ')[1];
          setThinking(false);
          if (resolverRef.current) {
            resolverRef.current(uci);
            resolverRef.current = null;
          }
        }
      };
      engine.onerror = () => setError('No se pudo cargar Stockfish');
      engine.postMessage('uci');
    } catch (e) {
      setError(String(e));
    }
    return () => { engine && engine.terminate(); };
  }, []);

  const bestMove = useCallback((fen, { level = 5, movetime = 500 } = {}) => {
    return new Promise((resolve, reject) => {
      const engine = engineRef.current;
      if (!engine || !ready) return reject(new Error('engine-not-ready'));
      resolverRef.current = resolve;
      setThinking(true);
      engine.postMessage('stop');
      engine.postMessage(`setoption name Skill Level value ${Math.max(0, Math.min(20, level))}`);
      engine.postMessage(`position fen ${fen}`);
      engine.postMessage(`go movetime ${movetime}`);
    });
  }, [ready]);

  return { ready, thinking, error, bestMove };
}
